import React, { useCallback } from "react";
import { Timeline } from "antd";
import { TrackedJob } from "../../../types/jobTypes";
import { AIJobType } from "../../../types/compassAITypes";
import {
  BatchAssessmentResult,
  AIBatchLinkageResponse,
  AIExtractionResponse,
  EmailAgentResponse,
} from "../../../types/compassAITypes";
import { usePost } from "../../../hooks/usePost";
import { useToast } from "../../../store/ToastProvider";
import { MESSAGES } from "../../../messages";
import { HeroKind } from "../../../enums/Modal";
import { statusBadgeColors, greyText } from "../../../styles";
import { BadgeStatus } from "../../../enums/Status";
import { formatReadable } from "../../../utils/formattingUtils";

import {
  CompassModal,
  ModalContent,
  ModalFooter,
  useCompassModal,
  defineModalConfig,
} from "../Modal";
import { CompassParagraph } from "../styledComponents";
import { NoteViewer } from "../Note/NoteViewer";

import { AssessmentResultsSummary } from "./AssessmentResultsSummary";
import { ExtractionResultsSummary } from "./ExtractionResultsSummary";
import { BatchLinkageResultsSummary } from "./BatchLinkageResultsSummary";
import {
  CompassGreenButton,
  CompassWhiteButton,
} from "../../buttons/styledComponents";

import {
  RowContainer,
  FlexibleContainer,
} from "../../containers/styledComponents";
import { Avatar } from "../../visuals/Avatar";

import aiIcon from "../../../assets/svg/compass_ai_modal_icon.svg";
import toolsIcon from "../../../assets/svg/tools.svg";
import greenCheckIcon from "../../../assets/svg/green_check_box.svg";
import successBg from "../../../assets/images/engagement_rating.png";

import { errorRed } from "../../../styles";

const formatToolName = (raw: string): string => {
  const idx = raw.lastIndexOf("_");
  if (idx === -1) return raw;
  return raw.slice(0, idx).includes("-") ? raw.slice(idx + 1) : raw;
};

const renderEmailAgentResults = (
  result: EmailAgentResponse,
): React.ReactNode => {
  const tools = result.tools_called ?? [];
  const plannedColor = statusBadgeColors[BadgeStatus.PLANNED].text;
  const progressColor = statusBadgeColors[BadgeStatus.IN_PROGRESS].text;
  const completeColor = statusBadgeColors[BadgeStatus.COMPLETE].text;

  const items = [
    {
      color: plannedColor,
      children: (
        <RowContainer jc="flex-start" ai="center">
          <Avatar username="Compass Atlas" />
          <CompassParagraph fontSize={12} color={greyText} ml={4} mb={0}>
            Agent started — Email {result.email_id?.slice(0, 8)}…
          </CompassParagraph>
        </RowContainer>
      ),
    },
    ...tools.map((tool, i) => ({
      color: progressColor,
      children: (
        <RowContainer jc="flex-start" ai="center">
          <Avatar username={`tool-${i}`} image={toolsIcon} />
          <CompassParagraph
            bold
            fontSize={12}
            color={greyText}
            mb={0}
            ml={4}
            mr={4}
          >
            Tool {i + 1}
          </CompassParagraph>
          <CompassParagraph fontSize={12} color={greyText} mb={0}>
            {formatReadable(formatToolName(tool).replace(/_/g, " "))}
          </CompassParagraph>
        </RowContainer>
      ),
    })),
    {
      color: completeColor,
      children: (
        <RowContainer jc="flex-start" ai="center">
          <Avatar username="agent-done" image={greenCheckIcon} />
          <CompassParagraph bold fontSize={12} color={greyText} ml={4} mb={0}>
            Agent completed
          </CompassParagraph>
        </RowContainer>
      ),
    },
  ];

  return (
    <FlexibleContainer fd="column" ai="flex-start" jc="flex-start" p={"12px"}>
      <CompassParagraph bold fontSize={14} mb={12}>
        Execution Timeline
      </CompassParagraph>
      <Timeline mode="left" items={items} />
      {result.agent_response && (
        <>
          <CompassParagraph bold fontSize={14} mb={6}>
            Agent Response
          </CompassParagraph>
          <NoteViewer
            content={result.agent_response}
            minHeight="80px"
            maxHeight="300px"
          />
        </>
      )}
    </FlexibleContainer>
  );
};

// =============================================================================
// CONFIG
// =============================================================================

type ResultsState = "results" | "empty";

const JOB_TYPE_TITLES: Record<AIJobType, string> = {
  ENGAGEMENT_ASSESSMENT: "Engagement Assessment Results",
  BATCH_LINKAGE: "Batch Linkage Results",
  ENGAGEMENT_EXTRACTION: "Extraction Results",
  EMAIL_AGENT: "Email Agent Results",
  PREPARE_PORTFOLIO_NEWS: "Company News Results",
};

const RESULTS_MODAL_CONFIG = defineModalConfig<ResultsState, unknown>({
  title: "Job Results",
  titleIcon: aiIcon,
  titleIconSize: 40,
  modalSize: "md",
  size: "lg",
  states: {
    results: {},
    empty: {
      hero: {
        kind: HeroKind.IMAGE,
        source: successBg,
        padding: 0,
        imageBorderRadius: 8,
        backgroundColor: "#fafafa",
        title: "No Results Available",
        titleColor: errorRed,
        description:
          "The result data for this job is no longer available. This can happen if the job completed some time ago.",
      },
    },
  },
});

// =============================================================================
// COMPONENT
// =============================================================================

export interface JobResultsModalProps {
  show: boolean;
  onHide: () => void;
  job: TrackedJob | null;
}

export const JobResultsModal: React.FC<JobResultsModalProps> = ({
  show,
  onHide,
  job,
}) => {
  const { addToast } = useToast();
  const { sendData: getDownloadUrl } = usePost<
    { url: string },
    { path: string }
  >();

  const payload = job?.resultPayload;
  const jobType = job?.jobType;
  const hasResults = !!payload && Object.keys(payload).length > 0;
  const state: ResultsState = hasResults ? "results" : "empty";

  const dynamicConfig = {
    ...RESULTS_MODAL_CONFIG,
    title: jobType ? JOB_TYPE_TITLES[jobType] : "Job Results",
  };

  const { modalProps } = useCompassModal({
    config: dynamicConfig,
    state,
    context: {},
  });

  const handleDownload = useCallback(
    async (path: string): Promise<void> => {
      const response = await getDownloadUrl("FILE_DOWNLOAD", { path });
      if (response?.url) {
        window.open(response.url, "_blank");
      } else {
        addToast(MESSAGES.COMPASS_AI.ERRORS.DOWNLOAD_FAILED, "error");
      }
    },
    [getDownloadUrl, addToast],
  );

  const renderResults = (): React.ReactNode => {
    if (!payload || !jobType) return null;

    switch (jobType) {
      case "ENGAGEMENT_ASSESSMENT":
        return (
          <AssessmentResultsSummary
            result={payload as unknown as BatchAssessmentResult}
          />
        );
      case "BATCH_LINKAGE":
        return (
          <BatchLinkageResultsSummary
            result={payload as unknown as AIBatchLinkageResponse}
          />
        );
      case "ENGAGEMENT_EXTRACTION":
        return (
          <ExtractionResultsSummary
            result={payload as unknown as AIExtractionResponse}
          />
        );
      case "EMAIL_AGENT":
        return renderEmailAgentResults(
          payload as unknown as EmailAgentResponse,
        );
      default:
        return null;
    }
  };

  const renderFooter = (): React.ReactNode => {
    if (!hasResults) {
      return (
        <ModalFooter
          onClose={onHide}
          closeText="Close"
          hideSubmit
          showDivider={false}
        />
      );
    }

    // Show download buttons based on job type
    if (jobType === "ENGAGEMENT_ASSESSMENT") {
      const result = payload as unknown as BatchAssessmentResult;
      return (
        <ModalFooter showDivider={false}>
          {result?.output_files?.assessments && (
            <CompassGreenButton
              onClick={(): void => {
                handleDownload(result.output_files.assessments);
              }}
            >
              Download Assessments
            </CompassGreenButton>
          )}
          {result?.output_files?.assessment_details && (
            <CompassWhiteButton
              onClick={(): void => {
                handleDownload(result.output_files.assessment_details);
              }}
            >
              Download Details
            </CompassWhiteButton>
          )}
          <CompassWhiteButton onClick={onHide}>Done</CompassWhiteButton>
        </ModalFooter>
      );
    }

    if (jobType === "BATCH_LINKAGE") {
      const result = payload as unknown as AIBatchLinkageResponse;
      return (
        <ModalFooter showDivider={false}>
          {result?.output_file_path && (
            <CompassGreenButton
              onClick={(): void => {
                handleDownload(result.output_file_path as string);
              }}
            >
              Download Results
            </CompassGreenButton>
          )}
          <CompassWhiteButton onClick={onHide}>Done</CompassWhiteButton>
        </ModalFooter>
      );
    }

    if (jobType === "ENGAGEMENT_EXTRACTION") {
      const result = payload as unknown as AIExtractionResponse;
      return (
        <ModalFooter showDivider={false}>
          {result?.output_engagements_csv && (
            <CompassGreenButton
              onClick={(): void => {
                handleDownload(result.output_engagements_csv);
              }}
            >
              Download Engagements
            </CompassGreenButton>
          )}
          {result?.output_updates_csv && (
            <CompassWhiteButton
              onClick={(): void => {
                handleDownload(result.output_updates_csv);
              }}
            >
              Download Updates
            </CompassWhiteButton>
          )}
          <CompassWhiteButton onClick={onHide}>Done</CompassWhiteButton>
        </ModalFooter>
      );
    }

    return (
      <ModalFooter
        onClose={onHide}
        closeText="Done"
        hideSubmit
        showDivider={false}
      />
    );
  };

  return (
    <CompassModal
      show={show}
      onHide={onHide}
      {...modalProps}
      footer={renderFooter()}
    >
      <ModalContent>{hasResults && renderResults()}</ModalContent>
    </CompassModal>
  );
};
